import React, { useEffect, useRef, useState } from 'react';
import './LiveStreamModal.css';
import { loadPlayer } from 'rtsp-relay/browser';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import RemoveIcon from '@mui/icons-material/Remove';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { Chip, IconButton } from '@mui/material';
import { formatDeviceId, getDeviceStreamUrl, getDevicePublishAudioUrl, cleanPrefixName } from '../../config/streams';

const LiveStreamModal = ({ isOpen, onClose, deviceId, deviceName, streamPrefix }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const audioRef = useRef(null);
    const [activeTab, setActiveTab] = useState('video');
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [playerState, setPlayerState] = useState('normal'); // 'normal' | 'maximized' | 'minimized'

    const [isPublishingAudio, setIsPublishingAudio] = useState(false);
    const mediaRecorderRef = useRef(null);
    const pubWsRef = useRef(null);
    const micStreamRef = useRef(null);

    const activePrefix = cleanPrefixName(streamPrefix || localStorage.getItem('stream_prefix') || 'Cam');
    const cleanPrefix = activePrefix;

    const formattedDevId = formatDeviceId(deviceId);

    const videoUrl = getDeviceStreamUrl('video', deviceId, activePrefix);
    const thermalUrl = getDeviceStreamUrl('thermal', deviceId, activePrefix);
    const audioUrl = getDeviceStreamUrl('audio', deviceId, activePrefix);
    const httpAudioStreamUrl = `${process.env.REACT_APP_BASE_URL || 'http://localhost:8080'}/admin/stream/audio?url=${encodeURIComponent(audioUrl)}`;
    const publishAudioUrl = getDevicePublishAudioUrl(deviceId, activePrefix);

    const streams = [
        { id: 'video', label: 'Video', icon: <VideocamIcon fontSize="small" />, url: videoUrl },
        { id: 'thermal', label: 'Thermal', icon: <ThermostatIcon fontSize="small" />, url: thermalUrl }
    ];

    const currentStream = streams.find(s => s.id === activeTab) || streams[0];

    const stopPublishingAudio = () => {
        setIsPublishingAudio(false);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try { mediaRecorderRef.current.stop(); } catch (e) {}
        }
        if (micStreamRef.current) {
            try {
                micStreamRef.current.getTracks().forEach(track => track.stop());
            } catch (e) {}
            micStreamRef.current = null;
        }
        if (pubWsRef.current) {
            try { pubWsRef.current.close(); } catch (e) {}
            pubWsRef.current = null;
        }
    };

    const startPublishingAudio = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStreamRef.current = stream;

            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.onended = () => {
                    console.warn('Microphone track ended');
                    stopPublishingAudio();
                };
            }

            const wsBaseUrl = process.env.REACT_APP_BASE_URL
                ? process.env.REACT_APP_BASE_URL.replace(/^http/, 'ws')
                : 'wss://api.imphantomresearch.com:8080';

            const wsUrl = `${wsBaseUrl}/admin/stream/publish-audio?url=${encodeURIComponent(publishAudioUrl)}`;
            const ws = new WebSocket(wsUrl);
            pubWsRef.current = ws;

            ws.onopen = () => {
                console.log('Audio publishing WebSocket connected to:', publishAudioUrl);
                setIsPublishingAudio(true);

                try {
                    let recorderOptions = {};
                    if (typeof MediaRecorder.isTypeSupported === 'function') {
                        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                            recorderOptions = { mimeType: 'audio/webm;codecs=opus' };
                        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                            recorderOptions = { mimeType: 'audio/webm' };
                        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                            recorderOptions = { mimeType: 'audio/mp4' };
                        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                            recorderOptions = { mimeType: 'audio/aac' };
                        }
                    }

                    let recorder;
                    try {
                        recorder = Object.keys(recorderOptions).length > 0
                            ? new MediaRecorder(stream, recorderOptions)
                            : new MediaRecorder(stream);
                    } catch (e) {
                        console.warn('MediaRecorder with mimeType options failed, falling back to default:', e);
                        recorder = new MediaRecorder(stream);
                    }

                    mediaRecorderRef.current = recorder;

                    recorder.ondataavailable = async (e) => {
                        if (e.data && e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
                            try {
                                const buffer = await e.data.arrayBuffer();
                                ws.send(buffer);
                            } catch (sendErr) {
                                console.error('Error sending audio data buffer:', sendErr);
                            }
                        }
                    };

                    recorder.onerror = (recErr) => {
                        console.error('MediaRecorder error:', recErr);
                        if (recorder.state === 'inactive') {
                            stopPublishingAudio();
                        }
                    };

                    recorder.start(500);
                } catch (recErr) {
                    console.error('MediaRecorder start error:', recErr);
                    alert('Microphone recording error: ' + (recErr.message || recErr));
                    stopPublishingAudio();
                }
            };

            ws.onerror = (err) => {
                console.error('Audio publishing WebSocket error:', err);
            };

            ws.onclose = (event) => {
                console.log('Audio publishing WebSocket closed:', event.code, event.reason);
                stopPublishingAudio();
            };
        } catch (err) {
            console.error('Failed to access microphone for audio publishing:', err);
            alert('Could not access microphone: ' + (err.name || err.message || err));
            stopPublishingAudio();
        }
    };



    const togglePublishAudio = () => {
        if (isPublishingAudio) {
            stopPublishingAudio();
        } else {
            startPublishingAudio();
        }
    };

    useEffect(() => {
        return () => {
            stopPublishingAudio();
        };
    }, []);

    // Safely manage canvas element creation and cleanup via containerRef to avoid React 'removeChild' errors
    useEffect(() => {
        if (!isOpen || activeTab === 'audio') return;

        let isSubscribed = true;
        let activePlayer = null;

        // Clean up previous canvas nodes safely in container
        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }

        if (containerRef.current) {
            const canvas = document.createElement('canvas');
            canvas.className = 'vlc_canvas';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.objectFit = 'contain';
            canvas.style.display = 'block';
            containerRef.current.appendChild(canvas);

            const wsBaseUrl = process.env.REACT_APP_BASE_URL
                ? process.env.REACT_APP_BASE_URL.replace(/^http/, 'ws')
                : 'wss://api.imphantomresearch.com:8080';

            const streamWsUrl = `${wsBaseUrl}/admin/stream/api/stream?url=${encodeURIComponent(currentStream.url)}`;

            try {
                loadPlayer({
                    url: streamWsUrl,
                    canvas: canvas,
                    audio: false,
                    pauseWhenHidden: false,
                    videoBufferSize: 2 * 1024 * 1024,
                    onDisconnect: () => {
                        if (isSubscribed) console.log('VLC Stream disconnected');
                    }
                }).then(p => {
                    if (isSubscribed) {
                        playerRef.current = p;
                        activePlayer = p;
                    }
                }).catch(err => {
                    console.error("VLC player load error:", err);
                });
            } catch (err) {
                console.error("VLC stream error:", err);
            }
        }

        return () => {
            isSubscribed = false;
            if (activePlayer && typeof activePlayer.destroy === 'function') {
                try {
                    activePlayer.destroy();
                } catch (e) {
                    // ignore
                }
            }
            playerRef.current = null;
            if (audioRef.current) {
                try {
                    audioRef.current.pause();
                    audioRef.current.src = '';
                } catch (e) {}
            }
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [isOpen, activeTab, currentStream.url, isMuted]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.muted = isMuted;
            if (!isMuted && isPlaying && audioRef.current.paused) {
                audioRef.current.play().catch(e => console.warn('Audio play error:', e));
            }
        }
    }, [isMuted, isPlaying]);

    useEffect(() => {
        if (isOpen && audioRef.current) {
            audioRef.current.src = httpAudioStreamUrl;
            audioRef.current.muted = isMuted;
            if (isPlaying) {
                audioRef.current.play().catch(err => {
                    console.warn('Live audio play error:', err);
                });
            }
        }
    }, [isOpen, httpAudioStreamUrl]);

    if (!isOpen) return null;

    const togglePlay = () => {
        if (isPlaying) {
            if (playerRef.current && typeof playerRef.current.pause === 'function') {
                playerRef.current.pause();
            }
            if (audioRef.current) {
                audioRef.current.pause();
            }
            setIsPlaying(false);
        } else {
            if (playerRef.current && typeof playerRef.current.play === 'function') {
                playerRef.current.play();
            }
            if (audioRef.current) {
                audioRef.current.src = httpAudioStreamUrl;
                audioRef.current.muted = isMuted;
                audioRef.current.play().catch(e => console.warn(e));
            }
            setIsPlaying(true);
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const toggleMaximize = () => {
        setPlayerState(prev => (prev === 'maximized' ? 'normal' : 'maximized'));
    };

    const toggleMinimize = () => {
        setPlayerState(prev => (prev === 'minimized' ? 'normal' : 'minimized'));
    };

    // If minimized, display floating bottom-right bar
    if (playerState === 'minimized') {
        return (
            <div className="vlc_mini_bar" onClick={() => setPlayerState('normal')}>
                <div className="vlc_mini_info">
                    <span className="vlc_live_dot"></span>
                    <span className="vlc_mini_title">VLC Media Player - {cleanPrefix}-{formattedDevId}</span>
                </div>
                <div className="vlc_mini_actions">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleMaximize(); }} style={{ color: '#ff9800' }}>
                        <OpenInFullIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ color: '#fff' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>
        );
    }

    return (
        <div className={`vlc_overlay ${playerState === 'maximized' ? 'maximized_overlay' : ''}`} onClick={onClose}>
            <div
                className={`vlc_player_window ${playerState === 'maximized' ? 'maximized_window' : ''}`}
                onClick={e => e.stopPropagation()}
            >
                {/* VLC Window Header */}
                <div className="vlc_header">
                    <div className="vlc_title">
                        <div className="vlc_cone_icon">▲</div>
                        <span>VLC Media Player - {deviceName || `${cleanPrefix}-${formattedDevId}`}</span>
                    </div>

                    <div className="vlc_window_controls">
                        <Chip label={`${cleanPrefix}-${formattedDevId}`} className="vlc_chip" size="small" />
                        
                        <IconButton onClick={toggleMinimize} size="small" title="Minimize" style={{ color: '#fff' }}>
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={toggleMaximize} size="small" title={playerState === 'maximized' ? 'Restore' : 'Maximize'} style={{ color: '#ff9800' }}>
                            {playerState === 'maximized' ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                        </IconButton>
                        <IconButton onClick={onClose} size="small" title="Close" style={{ color: '#ef5350' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                </div>

                {/* Stream Mode Selector Tabs */}
                <div className="vlc_tabs_bar">
                    {streams.map(s => (
                        <button
                            key={s.id}
                            className={`vlc_tab_btn ${activeTab === s.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(s.id)}
                        >
                            {s.icon}
                            <span>{s.label}</span>
                        </button>
                    ))}
                </div>

                {/* Video Stage */}
                <div className="vlc_screen_stage" style={{ position: 'relative' }}>
                    <div ref={containerRef} className="vlc_canvas_wrapper"></div>

                    {/* Synchronized Live Audio Stream */}
                    <audio
                        ref={audioRef}
                        src={httpAudioStreamUrl}
                        autoPlay
                        playsInline
                        muted={isMuted}
                        style={{ display: 'none' }}
                    />

                    {/* Live Stream Overlay Badge */}
                    <div className="vlc_live_badge">
                        <span className="vlc_red_pulse">●</span> LIVE
                    </div>

                    {/* Active Mic Broadcasting Badge */}
                    {isPublishingAudio && (
                        <div style={{ position: 'absolute', top: '10px', left: '10px', backgroundColor: 'rgba(239, 83, 80, 0.95)', color: '#fff', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px', zIndex: 10, boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
                            <MicIcon fontSize="small" /> <span>Broadcasting Audio (Mic Active)</span>
                        </div>
                    )}
                </div>

                {/* VLC Controls Bar */}
                <div className="vlc_controls_bar">
                    <div className="vlc_ctrl_left">
                        <IconButton onClick={togglePlay} className="vlc_play_btn">
                            {isPlaying ? <PauseIcon style={{ color: '#ff9800' }} /> : <PlayArrowIcon style={{ color: '#ff9800' }} />}
                        </IconButton>

                        <IconButton onClick={toggleMute} className="vlc_mute_btn">
                            {isMuted ? <VolumeOffIcon style={{ color: '#888' }} /> : <VolumeUpIcon style={{ color: '#fff' }} />}
                        </IconButton>

                        <IconButton onClick={togglePublishAudio} className="vlc_mic_btn" title={isPublishingAudio ? "Stop Microphone / Talkback" : "Start Microphone / Talkback"}>
                            {isPublishingAudio ? <MicIcon style={{ color: '#ef5350' }} /> : <MicOffIcon style={{ color: '#888' }} />}
                        </IconButton>
                    </div>

                    <div className="vlc_ctrl_center">
                        <div className="vlc_status_text">
                            <span>Status: {isPublishingAudio ? 'Publishing Mic Audio' : 'Streaming Live'}</span>
                            <span className="vlc_url_display">{isPublishingAudio ? publishAudioUrl : currentStream.url}</span>
                        </div>
                    </div>

                    <div className="vlc_ctrl_right">
                        <IconButton onClick={toggleMaximize} className="vlc_full_btn">
                            {playerState === 'maximized' ? <FullscreenExitIcon style={{ color: '#fff' }} /> : <FullscreenIcon style={{ color: '#fff' }} />}
                        </IconButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveStreamModal;

